/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useRef, useState } from 'react';

import { useRequest } from 'ahooks';
import { DeveloperApi } from '@adp-arch/bot-api';
import { Modal } from '@coze-arch/coze-design';
import { I18n } from '@adp-arch/i18n';

export interface UnpublishAgentParam {
  spaceId: string;
  agentId: string;
  name: string;
}

export const useUnpublishAgent = (props?: {
  onUnpublishSuccess?: (param: UnpublishAgentParam) => void;
}) => {
  const [visible, setVisible] = useState<boolean>(false);
  const unpublishParamsRef = useRef<UnpublishAgentParam>();

  const onCloseModal = () => {
    setVisible(false);
    unpublishParamsRef.current = undefined;
  };

  const { loading, runAsync } = useRequest(
    async (request: UnpublishAgentParam) => {
      const { spaceId, agentId } = request;
      await DeveloperApi.UnpublishBot({
        space_id: spaceId,
        bot_id: agentId,
      });
    },
    {
      manual: true,
      onSuccess: (_m, [p]) => {
        onCloseModal();
        props?.onUnpublishSuccess?.(p);
      },
    },
  );

  const onUnpublish = () => {
    if (!unpublishParamsRef.current) {
      return;
    }
    return runAsync(unpublishParamsRef.current);
  };

  return {
    modalContextHolder: (
      <Modal
        title={I18n.t('bot_unpublish_confirm_title')}
        visible={visible}
        onCancel={onCloseModal}
        onOk={onUnpublish}
        okText={I18n.t('Confirm')}
        cancelText={I18n.t('Cancel')}
        okButtonProps={{
          loading,
        }}
      >
        <p>{I18n.t('bot_unpublish_confirm_content')}</p>
      </Modal>
    ),
    unpublishAgent: (param: UnpublishAgentParam) => {
      setVisible(true);
      unpublishParamsRef.current = param;
    },
  };
};
